import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Car, Bell, LogOut, Building2, Users, BarChart3,
  Plus, Edit, Trash2, Loader2, User, MapPin, Clock,
  CheckCircle, XCircle, Calendar, QrCode, Download, ParkingCircle, UserPlus, Phone,
  FileText, CheckCircle2, AlertTriangle, PhoneCall, Play,
  TrendingUp, DollarSign, Activity, Timer, Star
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API, BACKEND_URL } from "@/App";
import { QRCodeSVG } from "qrcode.react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from "recharts";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [valetRequests, setValetRequests] = useState([]);
  const [driverBookings, setDriverBookings] = useState([]);
  const [walkinGuests, setWalkinGuests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [reports, setReports] = useState(null);
  const [dateRange, setDateRange] = useState("all"); // all, today, 7days, 30days
  const [ratings, setRatings] = useState([]);
  const [ratingStats, setRatingStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  // Venue form
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [venueName, setVenueName] = useState("");
  const [venueType, setVenueType] = useState("");
  const [venueAddress, setVenueAddress] = useState("");
  const [venuePhone, setVenuePhone] = useState("");
  const [venueImage, setVenueImage] = useState("");

  // QR Code dialog
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrVenue, setQrVenue] = useState(null);

  // Staff assignment
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedVenueForStaff, setSelectedVenueForStaff] = useState("");

  // Parking spots
  const [parkingSpotsDialogOpen, setParkingSpotsDialogOpen] = useState(false);
  const [parkingVenue, setParkingVenue] = useState(null);
  const [parkingSpots, setParkingSpots] = useState([]);
  const [spotPrefix, setSpotPrefix] = useState("");
  const [spotCount, setSpotCount] = useState("");
  const [spotZone, setSpotZone] = useState("");

  // Walk-in registration (admin can also register)
  const [walkinDialogOpen, setWalkinDialogOpen] = useState(false);
  const [walkinVenue, setWalkinVenue] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [carDetails, setCarDetails] = useState("");
  const [selectedSpot, setSelectedSpot] = useState("");
  const [guestNotes, setGuestNotes] = useState("");
  const [availableSpots, setAvailableSpots] = useState([]);

  // Waiver dialog
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);
  const [waiverGuest, setWaiverGuest] = useState(null);
  const [waiverText, setWaiverText] = useState("");
  const [waiverLoading, setWaiverLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  // Fetch waiver text on mount
  useEffect(() => {
    const fetchWaiverText = async () => {
      try {
        const response = await axios.get(`${API}/waiver/text`, { withCredentials: true });
        setWaiverText(response.data.waiver_text);
      } catch (error) {
        console.error("Error fetching waiver text:", error);
      }
    };
    fetchWaiverText();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [venuesRes, usersRes, staffRes, requestsRes, bookingsRes, walkinRes, analyticsRes, notifRes, unreadRes, reportsRes] = await Promise.all([
        axios.get(`${API}/venues`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/staff`, { withCredentials: true }),
        axios.get(`${API}/valet/venue-requests`, { withCredentials: true }),
        axios.get(`${API}/driver/all-bookings`, { withCredentials: true }),
        axios.get(`${API}/walkin/guests`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/notifications/unread-count`, { withCredentials: true }),
        axios.get(`${API}/admin/reports`, { withCredentials: true })
      ]);
      setVenues(venuesRes.data);
      setUsers(usersRes.data);
      setStaff(staffRes.data);
      setValetRequests(requestsRes.data);
      setDriverBookings(bookingsRes.data);
      setWalkinGuests(walkinRes.data);
      setAnalytics(analyticsRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
      setReports(reportsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fetch reports with date filter
  const fetchReports = useCallback(async (range) => {
    try {
      let params = "";
      const today = new Date().toISOString().split("T")[0];
      if (range === "today") {
        params = `?start_date=${today}&end_date=${today}`;
      } else if (range === "7days") {
        const start = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
        params = `?start_date=${start}&end_date=${today}`;
      } else if (range === "30days") {
        const start = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];
        params = `?start_date=${start}&end_date=${today}`;
      }
      const [reportsRes, ratingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/reports${params}`, { withCredentials: true }),
        axios.get(`${API}/ratings/all`, { withCredentials: true }),
        axios.get(`${API}/ratings/stats`, { withCredentials: true })
      ]);
      setReports(reportsRes.data);
      setRatings(ratingsRes.data);
      setRatingStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  }, []);

  useEffect(() => {
    fetchReports(dateRange);
  }, [dateRange, fetchReports]);

  const handleSaveVenue = async () => {
    if (!venueName || !venueType || !venueAddress) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const venueData = {
        name: venueName,
        venue_type: venueType,
        address: venueAddress,
        phone: venuePhone || null,
        image_url: venueImage || null
      };

      if (editingVenue) {
        await axios.put(`${API}/admin/venues/${editingVenue.venue_id}`, venueData, { withCredentials: true });
        toast.success("Venue updated successfully");
      } else {
        await axios.post(`${API}/admin/venues`, venueData, { withCredentials: true });
        toast.success("Venue created successfully");
      }

      setVenueDialogOpen(false);
      resetVenueForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save venue");
    }
  };

  const handleDeleteVenue = async (venueId) => {
    if (!window.confirm("Are you sure you want to delete this venue?")) return;
    
    try {
      await axios.delete(`${API}/admin/venues/${venueId}`, { withCredentials: true });
      toast.success("Venue deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete venue");
    }
  };

  const handleAssignStaff = async () => {
    if (!selectedStaff || !selectedVenueForStaff) {
      toast.error("Please select both staff and venue");
      return;
    }

    try {
      await axios.post(`${API}/admin/staff/assign`, {
        user_id: selectedStaff,
        venue_id: selectedVenueForStaff
      }, { withCredentials: true });
      toast.success("Staff assigned to venue");
      setAssignDialogOpen(false);
      setSelectedStaff("");
      setSelectedVenueForStaff("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign staff");
    }
  };

  // Parking spot functions
  const openParkingDialog = async (venue) => {
    setParkingVenue(venue);
    try {
      const response = await axios.get(`${API}/parking/spots/${venue.venue_id}`, { withCredentials: true });
      setParkingSpots(response.data);
    } catch (error) {
      setParkingSpots([]);
    }
    setParkingSpotsDialogOpen(true);
  };

  const handleCreateParkingSpots = async () => {
    if (!spotPrefix || !spotCount) {
      toast.error("Please enter prefix and count");
      return;
    }

    try {
      await axios.post(
        `${API}/parking/spots/bulk?venue_id=${parkingVenue.venue_id}&prefix=${spotPrefix}&count=${parseInt(spotCount)}&zone=${spotZone || ''}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Created ${spotCount} parking spots`);
      setSpotPrefix("");
      setSpotCount("");
      setSpotZone("");
      // Refresh spots
      const response = await axios.get(`${API}/parking/spots/${parkingVenue.venue_id}`, { withCredentials: true });
      setParkingSpots(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create spots");
    }
  };

  // Walk-in guest registration (Admin)
  const openWalkinDialog = async (venueId) => {
    setWalkinVenue(venueId);
    try {
      const response = await axios.get(`${API}/parking/available/${venueId}`, { withCredentials: true });
      setAvailableSpots(response.data);
    } catch (error) {
      setAvailableSpots([]);
    }
    setWalkinDialogOpen(true);
  };

  const handleRegisterWalkin = async () => {
    if (!guestName || !guestPhone || !carDetails || !walkinVenue) {
      toast.error("Please fill in guest name, phone, and car details");
      return;
    }

    try {
      await axios.post(`${API}/walkin/register`, {
        venue_id: walkinVenue,
        guest_name: guestName,
        guest_phone: guestPhone,
        car_details: carDetails,
        parking_spot: selectedSpot || null,
        notes: guestNotes || null
      }, { withCredentials: true });

      toast.success("Walk-in guest registered successfully!");
      setWalkinDialogOpen(false);
      setGuestName("");
      setGuestPhone("");
      setCarDetails("");
      setSelectedSpot("");
      setGuestNotes("");
      setWalkinVenue("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to register guest");
    }
  };

  // Waiver functions
  const openWaiverDialog = (guest) => {
    setWaiverGuest(guest);
    setWaiverDialogOpen(true);
  };

  const handleAcceptWaiver = async () => {
    if (!waiverGuest) return;
    setWaiverLoading(true);
    try {
      await axios.put(`${API}/waiver/${waiverGuest.guest_id}/accept?signature_type=verbal`, {}, { withCredentials: true });
      toast.success(`Waiver accepted by ${waiverGuest.guest_name}`);
      setWaiverDialogOpen(false);
      setWaiverGuest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept waiver");
    } finally {
      setWaiverLoading(false);
    }
  };

  const handleDeclineWaiver = async () => {
    if (!waiverGuest) return;
    setWaiverLoading(true);
    try {
      await axios.put(`${API}/waiver/${waiverGuest.guest_id}/decline`, {}, { withCredentials: true });
      toast.warning(`Waiver declined by ${waiverGuest.guest_name}. Service may be refused.`);
      setWaiverDialogOpen(false);
      setWaiverGuest(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline waiver");
    } finally {
      setWaiverLoading(false);
    }
  };

  const getWaiverBadge = (status) => {
    switch (status) {
      case "accepted":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 border text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Signed</Badge>;
      case "declined":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 border text-xs"><XCircle className="w-3 h-3 mr-1" />Declined</Badge>;
      case "pending":
      default:
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border text-xs"><AlertTriangle className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  // Walk-in guest status management (Admin)
  const updateWalkinStatus = async (guestId, newStatus) => {
    setUpdatingStatus(guestId);
    try {
      await axios.put(`${API}/walkin/${guestId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const requestWalkinCar = async (guestId) => {
    try {
      const response = await axios.put(`${API}/walkin/${guestId}/request-car`, {}, { withCredentials: true });
      toast.success(`Car requested! Spot: ${response.data.parking_spot || 'N/A'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request car");
    }
  };

  const callGuestViaApp = async (guestId, guestName) => {
    try {
      await axios.post(`${API}/walkin/${guestId}/call`, {}, { 
        withCredentials: true,
        params: { message: "Your car is ready. Please proceed to the exit." }
      });
      toast.success(`Notification sent to ${guestName}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    }
  };

  const getNextWalkinStatus = (currentStatus) => {
    const flow = { parked: "requested", requested: "fetching", fetching: "ready", ready: "completed" };
    return flow[currentStatus];
  };

  const getWalkinActionLabel = (status) => {
    const labels = { parked: "Request Car", requested: "Start Fetching", fetching: "Mark Ready", ready: "Complete" };
    return labels[status] || null;
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`, {}, { withCredentials: true });
      toast.success(`User role updated to ${newRole}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status, driverName = null) => {
    try {
      let url = `${API}/driver/booking/${bookingId}/status?status=${status}`;
      if (driverName) url += `&driver_name=${encodeURIComponent(driverName)}`;
      await axios.put(url, {}, { withCredentials: true });
      toast.success(`Booking status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update booking");
    }
  };

  const resetVenueForm = () => {
    setEditingVenue(null);
    setVenueName("");
    setVenueType("");
    setVenueAddress("");
    setVenuePhone("");
    setVenueImage("");
  };

  const openEditVenue = (venue) => {
    setEditingVenue(venue);
    setVenueName(venue.name);
    setVenueType(venue.venue_type);
    setVenueAddress(venue.address);
    setVenuePhone(venue.phone || "");
    setVenueImage(venue.image_url || "");
    setVenueDialogOpen(true);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      requested: { class: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "Requested" },
      acknowledged: { class: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Acknowledged" },
      fetching: { class: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30", label: "Fetching" },
      ready: { class: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Ready" },
      completed: { class: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", label: "Completed" },
      pending: { class: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "Pending" },
      confirmed: { class: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Confirmed" },
      in_progress: { class: "bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30", label: "In Progress" },
      cancelled: { class: "bg-red-500/20 text-red-400 border-red-500/30", label: "Cancelled" }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={`${config.class} border`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090A0F] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090A0F]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="crystal-header sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/logo.svg" alt="VeloPark" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full" />
            <div>
              <span className="font-display text-lg sm:text-xl font-semibold text-white block">VeloPark</span>
              <span className="text-xs text-[#D4AF37]">Admin Panel</span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:text-[#D4AF37] hover:bg-white/5 relative"
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>

              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 glass-card rounded-xl overflow-hidden z-50">
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <span className="font-medium text-white">Notifications</span>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#D4AF37] hover:text-[#F0C950] text-xs"
                        onClick={markAllRead}
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="p-6 text-center text-[#A1A1AA]">No notifications</div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.notification_id}
                          className={`p-4 border-b border-white/5 ${!notif.is_read ? 'bg-[#D4AF37]/5' : ''}`}
                        >
                          <p className="text-sm font-medium text-white">{notif.title}</p>
                          <p className="text-xs text-[#A1A1AA] mt-1">{notif.message}</p>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <User className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <span className="text-white text-sm hidden sm:block">{user?.name}</span>
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-6 sm:mb-8">
            <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="stat-venues">
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" />
                <span className="text-xs sm:text-sm text-[#A1A1AA]">Venues</span>
              </div>
              <p className="text-2xl sm:text-3xl font-semibold text-white">{analytics.total_venues}</p>
            </div>
            <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="stat-requests">
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <Car className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" />
                <span className="text-xs sm:text-sm text-[#A1A1AA]">Requests</span>
              </div>
              <p className="text-2xl sm:text-3xl font-semibold text-white">{analytics.total_valet_requests}</p>
            </div>
            <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="stat-bookings">
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" />
                <span className="text-xs sm:text-sm text-[#A1A1AA]">Bookings</span>
              </div>
              <p className="text-2xl sm:text-3xl font-semibold text-white">{analytics.total_driver_bookings}</p>
            </div>
            <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="stat-staff">
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]" />
                <span className="text-xs sm:text-sm text-[#A1A1AA]">Staff</span>
              </div>
              <p className="text-2xl sm:text-3xl font-semibold text-white">{analytics.total_staff}</p>
            </div>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="venues" className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-4 sm:mb-6">
            <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl inline-flex min-w-max sm:w-full sm:flex">
              <TabsTrigger 
                value="venues" 
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1"
                data-testid="venues-tab"
              >
                <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Venues
              </TabsTrigger>
              <TabsTrigger 
                value="staff" 
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1"
                data-testid="staff-tab"
              >
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Staff
              </TabsTrigger>
              <TabsTrigger 
                value="requests" 
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1"
                data-testid="requests-tab"
              >
                <Car className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Requests
              </TabsTrigger>
              <TabsTrigger 
                value="walkin" 
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1"
                data-testid="walkin-tab"
              >
                <UserPlus className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Walk-in</span><span className="sm:hidden">Guests</span> ({walkinGuests.length})
              </TabsTrigger>
              <TabsTrigger 
                value="bookings" 
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1"
                data-testid="bookings-tab"
              >
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Driver</span> Bookings
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1"
                data-testid="analytics-tab"
              >
                <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Analytics
              </TabsTrigger>
              <TabsTrigger 
                value="overview" 
                className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#090A0F] text-white rounded-lg px-2 sm:px-4 text-xs sm:text-sm flex-1"
                data-testid="overview-tab"
              >
                <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                Overview
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Venues Tab */}
          <TabsContent value="venues">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Manage Venues</h2>
              <Dialog open={venueDialogOpen} onOpenChange={(open) => {
                setVenueDialogOpen(open);
                if (!open) resetVenueForm();
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-venue-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Venue
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                      {editingVenue ? "Edit Venue" : "Add New Venue"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-white">Venue Name *</Label>
                      <Input
                        placeholder="e.g., Grand Restaurant"
                        value={venueName}
                        onChange={(e) => setVenueName(e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="venue-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Type *</Label>
                      <Select value={venueType} onValueChange={setVenueType}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="venue-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1B21] border-white/10">
                          <SelectItem value="restaurant" className="text-white hover:bg-white/10">Restaurant</SelectItem>
                          <SelectItem value="wedding" className="text-white hover:bg-white/10">Event Venue</SelectItem>
                          <SelectItem value="event" className="text-white hover:bg-white/10">Event Center</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Address *</Label>
                      <Input
                        placeholder="Full address"
                        value={venueAddress}
                        onChange={(e) => setVenueAddress(e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="venue-address-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Phone</Label>
                      <Input
                        placeholder="+1 234 567 8900"
                        value={venuePhone}
                        onChange={(e) => setVenuePhone(e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="venue-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Image URL</Label>
                      <Input
                        placeholder="https://..."
                        value={venueImage}
                        onChange={(e) => setVenueImage(e.target.value)}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="venue-image-input"
                      />
                    </div>
                    <Button 
                      className="w-full btn-primary py-6 rounded-xl"
                      onClick={handleSaveVenue}
                      data-testid="save-venue-btn"
                    >
                      {editingVenue ? "Update Venue" : "Create Venue"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {venues.length === 0 ? (
              <div className="control-panel rounded-xl p-12 text-center">
                <Building2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No venues yet</h3>
                <p className="text-[#A1A1AA]">Add your first venue to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {venues.map((venue) => (
                  <div 
                    key={venue.venue_id}
                    className="control-panel rounded-xl overflow-hidden"
                    data-testid={`venue-card-${venue.venue_id}`}
                  >
                    <div 
                      className="h-32 bg-cover bg-center"
                      style={{
                        backgroundImage: venue.image_url 
                          ? `url(${venue.image_url})` 
                          : `url(https://images.unsplash.com/photo-1753729349067-f317d8a80b9f?crop=entropy&cs=srgb&fm=jpg&w=400)`
                      }}
                    />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-medium text-white">{venue.name}</h3>
                        <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 border text-xs">
                          {venue.venue_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-[#A1A1AA] flex items-center gap-1 mb-4">
                        <MapPin className="w-3 h-3" />
                        {venue.address}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 border-white/10 text-white hover:bg-white/5"
                          onClick={() => openEditVenue(venue)}
                          data-testid={`edit-venue-${venue.venue_id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                          onClick={() => openParkingDialog(venue)}
                          data-testid={`parking-venue-${venue.venue_id}`}
                        >
                          <ParkingCircle className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                          onClick={() => openWalkinDialog(venue.venue_id)}
                          data-testid={`walkin-venue-${venue.venue_id}`}
                        >
                          <UserPlus className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                          onClick={() => {
                            setQrVenue(venue);
                            setQrDialogOpen(true);
                          }}
                          data-testid={`qr-venue-${venue.venue_id}`}
                        >
                          <QrCode className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleDeleteVenue(venue.venue_id)}
                          data-testid={`delete-venue-${venue.venue_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Parking Spots Dialog */}
            <Dialog open={parkingSpotsDialogOpen} onOpenChange={setParkingSpotsDialogOpen}>
              <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">
                    Parking Spots - {parkingVenue?.name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 mt-4">
                  {/* Add spots form */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Add Parking Spots</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Prefix (e.g., A, B, VIP)</Label>
                        <Input
                          placeholder="A"
                          value={spotPrefix}
                          onChange={(e) => setSpotPrefix(e.target.value)}
                          className="bg-white/5 border-white/10 text-white mt-1"
                          data-testid="spot-prefix-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Count</Label>
                        <Input
                          type="number"
                          placeholder="20"
                          value={spotCount}
                          onChange={(e) => setSpotCount(e.target.value)}
                          className="bg-white/5 border-white/10 text-white mt-1"
                          data-testid="spot-count-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-[#A1A1AA]">Zone (optional)</Label>
                        <Input
                          placeholder="Ground Floor"
                          value={spotZone}
                          onChange={(e) => setSpotZone(e.target.value)}
                          className="bg-white/5 border-white/10 text-white mt-1"
                          data-testid="spot-zone-input"
                        />
                      </div>
                    </div>
                    <Button 
                      className="btn-primary mt-3"
                      onClick={handleCreateParkingSpots}
                      data-testid="create-spots-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Spots (e.g., A1-A{spotCount || 'N'})
                    </Button>
                  </div>

                  {/* Existing spots grid */}
                  <div>
                    <h4 className="text-sm font-medium text-white mb-3">
                      Current Spots ({parkingSpots.length} total, {parkingSpots.filter(s => !s.is_occupied).length} available)
                    </h4>
                    {parkingSpots.length === 0 ? (
                      <p className="text-[#A1A1AA] text-sm">No parking spots configured yet</p>
                    ) : (
                      <div className="grid grid-cols-4 sm:grid-cols-6 sm:grid-cols-8 gap-2 max-h-60 overflow-y-auto">
                        {parkingSpots.map((spot) => (
                          <div
                            key={spot.spot_id}
                            className={`aspect-square rounded-lg flex flex-col items-center justify-center text-center p-1 border ${
                              spot.is_occupied 
                                ? 'bg-red-500/20 border-red-500/30' 
                                : 'bg-emerald-500/20 border-emerald-500/30'
                            }`}
                            title={spot.is_occupied ? spot.current_car : 'Available'}
                          >
                            <span className={`text-xs font-bold ${spot.is_occupied ? 'text-red-400' : 'text-emerald-400'}`}>
                              {spot.spot_name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Walk-in Registration Dialog */}
            <Dialog open={walkinDialogOpen} onOpenChange={setWalkinDialogOpen}>
              <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Register Walk-in Guest</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-white">Guest Name *</Label>
                    <Input
                      placeholder="John Doe"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="admin-guest-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Phone Number *</Label>
                    <Input
                      placeholder="+1 234 567 8900"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="admin-guest-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Car Details *</Label>
                    <Input
                      placeholder="Red Toyota Camry - ABC 1234"
                      value={carDetails}
                      onChange={(e) => setCarDetails(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="admin-car-details-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Parking Spot</Label>
                    <Select value={selectedSpot} onValueChange={setSelectedSpot}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white">
                        <SelectValue placeholder="Select spot" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1B21] border-white/10">
                        {availableSpots.map((spot) => (
                          <SelectItem key={spot.spot_id} value={spot.spot_name} className="text-white hover:bg-white/10">
                            {spot.spot_name} {spot.zone && `(${spot.zone})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Notes (Optional)</Label>
                    <Input
                      placeholder="VIP guest, special instructions..."
                      value={guestNotes}
                      onChange={(e) => setGuestNotes(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <Button 
                    className="w-full btn-primary py-6 rounded-xl"
                    onClick={handleRegisterWalkin}
                    data-testid="admin-submit-walkin-btn"
                  >
                    Register Guest
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* QR Code Dialog */}
            <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
              <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Venue QR Code</DialogTitle>
                </DialogHeader>
                {qrVenue && (
                  <div className="flex flex-col items-center py-6">
                    <div className="bg-white p-4 rounded-2xl mb-6">
                      <QRCodeSVG
                        value={`${window.location.origin}/guest?venue=${qrVenue.venue_id}`}
                        size={200}
                        level="L"
                        fgColor="#090A0F"
                        bgColor="#FFFFFF"
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{qrVenue.name}</h3>
                    <p className="text-sm text-[#A1A1AA] text-center mb-4">
                      Guests scan this code to instantly request their car
                    </p>
                    <div className="flex gap-3">
                      <Button
                        className="btn-primary"
                        onClick={() => {
                          const svg = document.querySelector('.qr-download-target svg');
                          if (svg) {
                            const svgData = new XMLSerializer().serializeToString(svg);
                            const canvas = document.createElement("canvas");
                            const ctx = canvas.getContext("2d");
                            const img = new Image();
                            img.onload = () => {
                              canvas.width = img.width;
                              canvas.height = img.height;
                              ctx.fillStyle = "#FFFFFF";
                              ctx.fillRect(0, 0, canvas.width, canvas.height);
                              ctx.drawImage(img, 0, 0);
                              const pngFile = canvas.toDataURL("image/png");
                              const downloadLink = document.createElement("a");
                              downloadLink.download = `${qrVenue.name}-QR.png`;
                              downloadLink.href = pngFile;
                              downloadLink.click();
                            };
                            img.src = "data:image/svg+xml;base64," + btoa(svgData);
                          }
                          toast.success("QR Code downloaded!");
                        }}
                        data-testid="download-qr-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PNG
                      </Button>
                    </div>
                    <div className="qr-download-target hidden">
                      <QRCodeSVG
                        value={`${window.location.origin}/guest?venue=${qrVenue.venue_id}`}
                        size={400}
                        level="L"
                        fgColor="#090A0F"
                        bgColor="#FFFFFF"
                      />
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white">Staff Management</h2>
              <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="assign-staff-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Assign to Venue
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0D0E14] border-white/10 text-white">
                  <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Assign Staff to Venue</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-white">Select Staff Member</Label>
                      <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-staff">
                          <SelectValue placeholder="Choose staff" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1B21] border-white/10">
                          {staff.map((s) => (
                            <SelectItem key={s.user_id} value={s.user_id} className="text-white hover:bg-white/10">
                              {s.name} ({s.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Select Venue</Label>
                      <Select value={selectedVenueForStaff} onValueChange={setSelectedVenueForStaff}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-venue-for-staff">
                          <SelectValue placeholder="Choose venue" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1B21] border-white/10">
                          {venues.map((v) => (
                            <SelectItem key={v.venue_id} value={v.venue_id} className="text-white hover:bg-white/10">
                              {v.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      className="w-full btn-primary py-6 rounded-xl"
                      onClick={handleAssignStaff}
                      data-testid="confirm-assign-btn"
                    >
                      Assign Staff
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="control-panel rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Name</TableHead>
                    <TableHead className="text-[#A1A1AA]">Email</TableHead>
                    <TableHead className="text-[#A1A1AA]">Role</TableHead>
                    <TableHead className="text-[#A1A1AA]">Assigned Venue</TableHead>
                    <TableHead className="text-[#A1A1AA]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.filter(u => u.user_id !== user?.user_id).map((u) => (
                    <TableRow key={u.user_id} className="border-white/10" data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="text-white font-medium">{u.name}</TableCell>
                      <TableCell className="text-[#A1A1AA]">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={
                          u.role === "admin" ? "bg-purple-500/20 text-purple-400 border-purple-500/30" :
                          u.role === "valet_staff" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                          "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"
                        }>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#A1A1AA]">
                        {u.venue_id ? venues.find(v => v.venue_id === u.venue_id)?.name || "-" : "-"}
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={u.role} 
                          onValueChange={(newRole) => handleUpdateUserRole(u.user_id, newRole)}
                        >
                          <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1B21] border-white/10">
                            <SelectItem value="customer" className="text-white hover:bg-white/10">Customer</SelectItem>
                            <SelectItem value="valet_staff" className="text-white hover:bg-white/10">Valet Staff</SelectItem>
                            <SelectItem value="admin" className="text-white hover:bg-white/10">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Valet Requests Tab */}
          <TabsContent value="requests">
            <h2 className="text-xl font-semibold text-white mb-6">All Valet Requests</h2>
            <div className="control-panel rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Customer</TableHead>
                    <TableHead className="text-[#A1A1AA]">Venue</TableHead>
                    <TableHead className="text-[#A1A1AA]">Car Details</TableHead>
                    <TableHead className="text-[#A1A1AA]">Status</TableHead>
                    <TableHead className="text-[#A1A1AA]">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {valetRequests.map((request) => (
                    <TableRow key={request.request_id} className="border-white/10" data-testid={`request-row-${request.request_id}`}>
                      <TableCell className="text-white font-medium">{request.user_name}</TableCell>
                      <TableCell className="text-[#A1A1AA]">{request.venue_name}</TableCell>
                      <TableCell className="text-[#A1A1AA]">{request.car_details}</TableCell>
                      <TableCell>{getStatusBadge(request.status)}</TableCell>
                      <TableCell className="text-[#A1A1AA]">
                        {new Date(request.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Walk-in Guests Tab */}
          <TabsContent value="walkin">
            <h2 className="text-xl font-semibold text-white mb-6">Walk-in Guests</h2>
            {walkinGuests.length === 0 ? (
              <div className="control-panel rounded-xl p-12 text-center">
                <UserPlus className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No walk-in guests</h3>
                <p className="text-[#A1A1AA]">Register walk-in guests from the venue card</p>
              </div>
            ) : (
              <div className="space-y-3">
                {walkinGuests.map((guest) => {
                  const nextStatus = getNextWalkinStatus(guest.status);
                  const actionLabel = getWalkinActionLabel(guest.status);
                  return (
                    <div key={guest.guest_id} className={`control-panel rounded-xl p-5 ${guest.status === "fetching" ? "ring-2 ring-[#D4AF37]/50" : ""}`} data-testid={`admin-walkin-card-${guest.guest_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                            <Car className="w-5 h-5 text-[#D4AF37]" />
                          </div>
                          <div>
                            <h3 className="font-medium text-white">{guest.guest_name}</h3>
                            <p className="text-sm text-[#A1A1AA]">{guest.venue_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getWaiverBadge(guest.waiver_status)}
                          {getStatusBadge(guest.status)}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-[#A1A1AA]">Phone</p>
                          <p className="text-sm text-white flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {guest.guest_phone}
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-[#A1A1AA]">Car</p>
                          <p className="text-sm text-white">{guest.car_details}</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-[#A1A1AA]">Token</p>
                          <p className="text-sm text-emerald-400 font-mono">{guest.parking_token}</p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2">
                          <p className="text-xs text-[#A1A1AA]">Spot</p>
                          <p className="text-sm text-[#D4AF37]">{guest.parking_spot || '-'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Waiver button */}
                        {guest.waiver_status === "pending" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                            onClick={() => openWaiverDialog(guest)}
                            data-testid={`admin-waiver-btn-${guest.guest_id}`}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            Get Waiver
                          </Button>
                        )}
                        {/* Call guest */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                          onClick={() => callGuestViaApp(guest.guest_id, guest.guest_name)}
                          data-testid={`admin-call-guest-${guest.guest_id}`}
                        >
                          <PhoneCall className="w-3 h-3 mr-1" />
                          Notify Guest
                        </Button>
                        {/* Status action */}
                        {actionLabel && guest.status !== "completed" && (
                          <Button
                            size="sm"
                            className="btn-primary"
                            onClick={() => guest.status === "parked" ? requestWalkinCar(guest.guest_id) : updateWalkinStatus(guest.guest_id, nextStatus)}
                            disabled={updatingStatus === guest.guest_id}
                            data-testid={`admin-action-${guest.guest_id}`}
                          >
                            {updatingStatus === guest.guest_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
                            {actionLabel}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Driver Bookings Tab */}
          <TabsContent value="bookings">
            <h2 className="text-xl font-semibold text-white mb-6">Driver Bookings</h2>
            <div className="control-panel rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Customer</TableHead>
                    <TableHead className="text-[#A1A1AA]">Venue</TableHead>
                    <TableHead className="text-[#A1A1AA]">Pickup</TableHead>
                    <TableHead className="text-[#A1A1AA]">Destination</TableHead>
                    <TableHead className="text-[#A1A1AA]">Status</TableHead>
                    <TableHead className="text-[#A1A1AA]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {driverBookings.map((booking) => (
                    <TableRow key={booking.booking_id} className="border-white/10" data-testid={`booking-row-${booking.booking_id}`}>
                      <TableCell className="text-white font-medium">{booking.user_name}</TableCell>
                      <TableCell className="text-[#A1A1AA]">{booking.venue_name}</TableCell>
                      <TableCell className="text-[#A1A1AA]">
                        <div>{booking.pickup_location}</div>
                        <div className="text-xs text-[#D4AF37]">
                          {new Date(booking.pickup_time).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-[#A1A1AA]">{booking.destination}</TableCell>
                      <TableCell>{getStatusBadge(booking.status)}</TableCell>
                      <TableCell>
                        {booking.status === "pending" && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 h-8"
                              onClick={() => handleUpdateBookingStatus(booking.booking_id, "confirmed", "Assigned Driver")}
                              data-testid={`confirm-booking-${booking.booking_id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              className="bg-red-500/20 text-red-400 hover:bg-red-500/30 h-8"
                              onClick={() => handleUpdateBookingStatus(booking.booking_id, "cancelled")}
                              data-testid={`cancel-booking-${booking.booking_id}`}
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        {booking.status === "confirmed" && (
                          <Button
                            size="sm"
                            className="bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 h-8"
                            onClick={() => handleUpdateBookingStatus(booking.booking_id, "in_progress")}
                            data-testid={`start-booking-${booking.booking_id}`}
                          >
                            Start Trip
                          </Button>
                        )}
                        {booking.status === "in_progress" && (
                          <Button
                            size="sm"
                            className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 h-8"
                            onClick={() => handleUpdateBookingStatus(booking.booking_id, "completed")}
                            data-testid={`complete-booking-${booking.booking_id}`}
                          >
                            Complete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
              <h2 className="text-xl font-semibold text-white">Reports & Analytics</h2>
              {/* Date Range Filter */}
              <div className="flex bg-white/5 border border-white/10 rounded-xl p-1 gap-0.5" data-testid="date-range-filter">
                {[
                  { value: "all", label: "All Time" },
                  { value: "today", label: "Today" },
                  { value: "7days", label: "7 Days" },
                  { value: "30days", label: "30 Days" }
                ].map((opt) => (
                  <button
                    key={opt.value}
                    className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      dateRange === opt.value ? "bg-[#D4AF37] text-[#090A0F]" : "text-[#A1A1AA] hover:text-white"
                    }`}
                    onClick={() => setDateRange(opt.value)}
                    data-testid={`filter-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {reports ? (
              <div className="space-y-6">
                {/* Key Metrics Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                  <div className="control-panel rounded-xl p-4" data-testid="metric-avg-wait">
                    <div className="flex items-center gap-2 mb-1">
                      <Timer className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-xs text-[#A1A1AA]">Avg Wait</span>
                    </div>
                    <p className="text-2xl font-semibold text-white">{reports.avg_wait_minutes}<span className="text-sm text-[#A1A1AA]"> min</span></p>
                  </div>
                  <div className="control-panel rounded-xl p-4" data-testid="metric-revenue">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-[#A1A1AA]">Revenue</span>
                    </div>
                    <p className="text-2xl font-semibold text-emerald-400">₹{reports.total_revenue.toFixed(0)}</p>
                  </div>
                  <div className="control-panel rounded-xl p-4" data-testid="metric-completed">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-xs text-[#A1A1AA]">Completed</span>
                    </div>
                    <p className="text-2xl font-semibold text-white">{reports.total_completed}</p>
                  </div>
                  <div className="control-panel rounded-xl p-4" data-testid="metric-active">
                    <div className="flex items-center gap-2 mb-1">
                      <Activity className="w-4 h-4 text-amber-400" />
                      <span className="text-xs text-[#A1A1AA]">Active Now</span>
                    </div>
                    <p className="text-2xl font-semibold text-amber-400">{reports.total_active}</p>
                  </div>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Peak Hours Chart */}
                  <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="chart-peak-hours">
                    <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#D4AF37]" />
                      Peak Hours
                    </h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={reports.peak_hours.map((count, hour) => ({ hour: `${hour}:00`, count }))}>
                        <XAxis dataKey="hour" tick={{ fill: '#A1A1AA', fontSize: 10 }} interval={3} />
                        <YAxis tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                        <Tooltip 
                          contentStyle={{ background: '#1a1b23', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                          labelStyle={{ color: '#D4AF37' }}
                        />
                        <Bar dataKey="count" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Daily Requests (Last 7 Days) */}
                  <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="chart-daily-requests">
                    <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
                      Requests (Last 7 Days)
                    </h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={Object.entries(reports.daily_requests).sort().map(([date, count]) => ({ date: date.slice(5), count }))}>
                        <XAxis dataKey="date" tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                        <Tooltip 
                          contentStyle={{ background: '#1a1b23', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                          labelStyle={{ color: '#D4AF37' }}
                        />
                        <Area type="monotone" dataKey="count" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.15} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Revenue Breakdown */}
                {Object.keys(reports.revenue_by_package).length > 0 && (
                  <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="chart-revenue">
                    <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-400" />
                      Revenue by Package ({reports.paid_bookings_count} bookings)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {Object.entries(reports.revenue_by_package).map(([pkg, amount]) => (
                        <div key={pkg} className="bg-white/5 rounded-lg p-4 text-center">
                          <p className="text-xs text-[#A1A1AA] mb-1">{pkg}</p>
                          <p className="text-xl font-semibold text-emerald-400">₹{amount.toFixed(0)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ratings Section */}
                {ratingStats && (
                  <div className="control-panel rounded-xl p-4 sm:p-6" data-testid="ratings-section">
                    <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
                      <Star className="w-4 h-4 text-[#D4AF37]" />
                      Customer Ratings ({ratingStats.total_ratings} total)
                    </h3>
                    {ratingStats.total_ratings === 0 ? (
                      <p className="text-sm text-[#A1A1AA]">No ratings yet. Ratings appear after customers rate completed services.</p>
                    ) : (
                      <div className="space-y-4">
                        {/* Rating averages */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-white/5 rounded-lg p-3 text-center">
                            <p className="text-xs text-[#A1A1AA]">Overall</p>
                            <p className="text-2xl font-semibold text-[#D4AF37]">{ratingStats.average_rating}</p>
                            <div className="flex justify-center mt-1">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} className={`w-3 h-3 ${s <= Math.round(ratingStats.average_rating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'}`} />
                              ))}
                            </div>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3 text-center">
                            <p className="text-xs text-[#A1A1AA]">Valet</p>
                            <p className="text-2xl font-semibold text-white">{ratingStats.valet_avg || '-'}</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-3 text-center">
                            <p className="text-xs text-[#A1A1AA]">Driver</p>
                            <p className="text-2xl font-semibold text-white">{ratingStats.driver_avg || '-'}</p>
                          </div>
                        </div>
                        {/* Star distribution */}
                        <div className="space-y-1.5">
                          {[5,4,3,2,1].map(star => {
                            const count = ratingStats.distribution[star] || 0;
                            const pct = ratingStats.total_ratings > 0 ? (count / ratingStats.total_ratings * 100) : 0;
                            return (
                              <div key={star} className="flex items-center gap-2">
                                <span className="text-xs text-[#A1A1AA] w-4">{star}</span>
                                <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
                                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-[#A1A1AA] w-6 text-right">{count}</span>
                              </div>
                            );
                          })}
                        </div>
                        {/* Recent reviews */}
                        {ratings.filter(r => r.review).length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs text-[#A1A1AA] mb-2">Recent Reviews</p>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {ratings.filter(r => r.review).slice(0, 5).map(r => (
                                <div key={r.rating_id} className="bg-white/5 rounded-lg p-3">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm text-white font-medium">{r.user_name}</span>
                                    <div className="flex">
                                      {[1,2,3,4,5].map(s => (
                                        <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'}`} />
                                      ))}
                                    </div>
                                    <Badge className="text-[10px] bg-white/10 border-0 text-[#A1A1AA]">{r.service_type}</Badge>
                                  </div>
                                  <p className="text-xs text-[#A1A1AA]">{r.review}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
              </div>
            )}
          </TabsContent>

          {/* Multi-Venue Overview Tab */}
          <TabsContent value="overview">
            <h2 className="text-xl font-semibold text-white mb-6">Multi-Venue Overview</h2>
            {reports?.venue_metrics ? (
              <div className="space-y-4">
                {reports.venue_metrics.length === 0 ? (
                  <div className="control-panel rounded-xl p-12 text-center">
                    <Building2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No venue data yet</h3>
                    <p className="text-[#A1A1AA]">Create venues and serve customers to see metrics here</p>
                  </div>
                ) : (
                  <>
                    {/* Summary Bar */}
                    <div className="control-panel rounded-xl p-4 flex flex-wrap items-center gap-4 sm:gap-8" data-testid="overview-summary">
                      <div>
                        <p className="text-xs text-[#A1A1AA]">Total Venues</p>
                        <p className="text-xl font-semibold text-white">{reports.venue_metrics.length}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#A1A1AA]">Total Requests</p>
                        <p className="text-xl font-semibold text-white">{reports.venue_metrics.reduce((sum, v) => sum + v.total_requests, 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#A1A1AA]">Total Walk-ins</p>
                        <p className="text-xl font-semibold text-white">{reports.venue_metrics.reduce((sum, v) => sum + v.total_walkins, 0)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#A1A1AA]">Total Revenue</p>
                        <p className="text-xl font-semibold text-emerald-400">₹{reports.total_revenue.toFixed(0)}</p>
                      </div>
                    </div>

                    {/* Venue Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {reports.venue_metrics.map((venue) => (
                        <div key={venue.venue_id} className="control-panel rounded-xl p-4 sm:p-5" data-testid={`venue-metric-${venue.venue_id}`}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-[#D4AF37]" />
                              </div>
                              <div>
                                <h3 className="font-medium text-white text-sm sm:text-base">{venue.venue_name}</h3>
                                <p className="text-xs text-[#A1A1AA] capitalize">{venue.venue_type}</p>
                              </div>
                            </div>
                            {venue.active_requests > 0 && (
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 border text-xs">
                                {venue.active_requests} active
                              </Badge>
                            )}
                          </div>

                          <div className="grid grid-cols-3 gap-2 mb-3">
                            <div className="bg-white/5 rounded-lg p-2 text-center">
                              <p className="text-[10px] text-[#A1A1AA]">Requests</p>
                              <p className="text-lg font-semibold text-white">{venue.total_requests}</p>
                            </div>
                            <div className="bg-white/5 rounded-lg p-2 text-center">
                              <p className="text-[10px] text-[#A1A1AA]">Walk-ins</p>
                              <p className="text-lg font-semibold text-white">{venue.total_walkins}</p>
                            </div>
                            <div className="bg-white/5 rounded-lg p-2 text-center">
                              <p className="text-[10px] text-[#A1A1AA]">Revenue</p>
                              <p className="text-lg font-semibold text-emerald-400">₹{venue.revenue}</p>
                            </div>
                          </div>

                          {/* Parking utilization bar */}
                          {venue.total_spots > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-[#A1A1AA]">Parking Spots</span>
                                <span className="text-xs text-white">{venue.occupied_spots}/{venue.total_spots} ({venue.spot_utilization}%)</span>
                              </div>
                              <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${venue.spot_utilization > 80 ? 'bg-red-500' : venue.spot_utilization > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                  style={{ width: `${venue.spot_utilization}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Waiver Dialog */}
        <Dialog open={waiverDialogOpen} onOpenChange={setWaiverDialogOpen}>
          <DialogContent className="bg-[#0D0E14] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
                Liability Waiver
              </DialogTitle>
              <p className="text-sm text-[#A1A1AA]">Review and confirm the liability waiver with the guest.</p>
            </DialogHeader>
            {waiverGuest && (
              <div className="space-y-4 mt-2">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-sm text-[#A1A1AA]">Guest</p>
                  <p className="text-white font-medium">{waiverGuest.guest_name}</p>
                  <p className="text-sm text-[#A1A1AA] mt-1">Car: {waiverGuest.car_details}</p>
                </div>
                <ScrollArea className="h-48 bg-white/5 rounded-lg p-4">
                  <pre className="text-sm text-[#A1A1AA] whitespace-pre-wrap font-sans">{waiverText}</pre>
                </ScrollArea>
                <DialogFooter className="flex gap-3 sm:gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={handleDeclineWaiver}
                    disabled={waiverLoading}
                    data-testid="admin-decline-waiver-btn"
                  >
                    {waiverLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                    Decline
                  </Button>
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={handleAcceptWaiver}
                    disabled={waiverLoading}
                    data-testid="admin-accept-waiver-btn"
                  >
                    {waiverLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    Accept & Sign
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminDashboard;
